import { Div, LabelProps } from '@kizi/core-components';
import { Checkbox, CheckboxProps } from '@kizi/shared-components';
import React from 'react';
import { isMobile } from '../../utils/global';
import { useFieldTracking } from '../../utils/hooks';

export interface TileCheckboxButtonProps {
  label: React.ReactElement;
  checkbox?: CheckboxProps;
  hideCheckbox?: boolean;
  style?: any;
  buttonMaxWidth?: string;
  labelProps?: LabelProps;
  fieldName?: string;
}

export const TileCheckboxButton: React.VFC<TileCheckboxButtonProps> = ({
  label,
  checkbox,
  hideCheckbox,
  style,
  buttonMaxWidth,
  labelProps,
  fieldName,
}) => {
  const { trackEvent } = useFieldTracking();

  const handleChange = React.useCallback(() => {
    trackEvent(fieldName)({
      target: {
        ga4event: 'click',
        value: checkbox?.value as string,
        checked: !checkbox?.checked,
        localName: 'checkbox',
      },
    });

    checkbox?.onChange?.({
      target: {
        name: checkbox.name as string,
        value: checkbox.value as string,
        checked: !checkbox.checked,
      },
    } as React.ChangeEvent<any>);
    checkbox?.onClick?.({
      target: {
        name: checkbox.name as string,
        value: checkbox.value as string,
        checked: !checkbox.checked,
      },
    } as any);
  }, [checkbox]);

  const handleClick = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (event) => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }
      handleChange();
    },
    [handleChange],
  );

  const cardBorderColor = checkbox?.checked ? '#007BFF' : 'transparent';
  const cardBackroundColor = cardBorderColor;
  const fillColor = cardBorderColor;
  const radioBorderColor = checkbox?.checked ? 'white' : null;
  const spanInputProps = {
    backgroundColor: checkbox?.checked ? 'white!important' : null,
  };
  const css = checkbox?.checked
    ? {
        borderColor: 'white',
        '&:hover': { bg: '#007BFF', borderColor: '#007BFF' },
      }
    : null;
  const cursor = checkbox ? 'pointer' : '';

  return (
    <Div
      position="relative"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      variant="checkboxTile"
      borderWidth={2}
      borderStyle="solid"
      alignItems="center"
      style={{
        ...style,
        boxSizing: 'border-box',
        cursor: hideCheckbox ? 'default' : cursor,
      }}
      background={cardBackroundColor}
      borderColor={cardBorderColor}
      onClick={hideCheckbox ? undefined : handleClick}
      width={hideCheckbox ? 'auto' : '100%'}
      maxWidth={isMobile() ? null : buttonMaxWidth}
    >
      <Div
        display={hideCheckbox ? 'none' : 'flex'}
        alignItems="center"
        width={22}
      >
        <Checkbox
          id={checkbox?.name}
          variant="main"
          {...checkbox}
          mx={0}
          width={20}
          height={20}
          containerProps={{
            ...checkbox?.containerProps,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row-reverse',
            justifyContent: 'center',
          }}
          label=""
          onChange={handleChange}
          borderColor={radioBorderColor}
          borderWidth="1px"
          iconProps={{ fill: fillColor }}
          spanInputProps={spanInputProps}
          css={css}
        />
      </Div>
      <Div
        flex={10}
        flexDirection="column"
        marginLeft={hideCheckbox ? 0 : 3}
        variant={checkbox?.checked ? 'labelChecked' : ''}
        {...labelProps}
      >
        {label}
      </Div>
    </Div>
  );
};
